import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Intern process part 2 – (my first) webinar, about React context",
  "description": "",
  "published": "2018-06-14T00:00:00.000Z",
  "redirect_from": ["/blog/intern-process-part-2-first-webinar-react-context/swizec/8458"],
  "categories": "Business, Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "zCYuhqW2Qeo",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`My first webinar! How I hate the word, but that's what it was.`}</p>
    <p>{`The plan was to get a bunch of `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/tech-intern-job/swizec/8454"
      }}>{`prospective tech interns`}</a>{` together on a stream, teach them a React concept, and give them something to build. Sunday night, I'm seeing what they build and figuring out who I want to work with.`}</p>
    <p>{`The goal?`}</p>
    <p>{`To judge people's `}<a parentName="p" {...{
        "href": "http://qr.ae/TUphry"
      }}>{`slope vs. y-intercept`}</a>{`. Are they fast learners, or do they just know a lot? I want to hire someone who learns fast, not someone who's already a pro. That way I'm working with a future pro and helping them get there.`}</p>
    <p>{`Imagine `}<inlineCode parentName="p">{`y`}</inlineCode>{` is your skill and `}<inlineCode parentName="p">{`x`}</inlineCode>{` is time. Slope always beats out y-intercept in the long run.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`a little bit of slope makes up for a lot of y-intercept `}<a parentName="p" {...{
          "href": "https://t.co/ZiJc29jyOA"
        }}>{`pic.twitter.com/ZiJc29jyOA`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1007281218570711040"
      }}>{`June 14, 2018`}</a></blockquote>
    <h2 {...{
      "id": "what-interns-learned"
    }}>{`What interns learned`}</h2>
    <p>{`We learned about the new React context API and built a `}<a parentName="p" {...{
        "href": "https://swizec.github.io/react-context-example/"
      }}>{`silly little app`}</a>{` that shows off two benefits of context: 1. Shared state between components 2. No prop drilling`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.github.io/react-context-example/"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2018/06/context-app.gif",
          "alt": null
        }}></img></a></p>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/react-context-example"
      }}>{`Here’s the source on GitHub.`}</a></p>
    <p>{`We used `}<inlineCode parentName="p">{`React.createContext()`}</inlineCode>{` to create a context:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const LockContext = React.createContext()
\`\`\`

And a top level component to hold the state and callback we wanted to share. It's like a Redux or MobX store, you want your state and your callbacks to travel together.

\`\`\`javascript
class ContextualThing extends React.Component {
    state = {
        locked: false
    };

    toggleLock = () =>
        this.setState({
            locked: !this.state.locked
        });

    render() {
        const { locked } = this.state;

        return (
            <div>
                 This is a lock
                <p style={{ fontsize: "2em" }}>
                    {locked ? <b>Locked!</b> : "Open"}
                </p>
                <lockcontext class="provider" value={{ locked, togglelock: this.togglelock }}>

                        {[1, 2, 3, 2, 1].map(n => <togglerow n={n}>)}
                    </togglerow><table></table>
                </lockcontext>
            </div>
        );
    }
}
`}</code></pre>
    <p>{`When state and callbacks go hand in hand, they're easier to manage. Helps you think in state machines.`}</p>
    <p>{`But I forgot to mention the state machine part in the webinar 😅`}</p>
    <p>{`Each `}<inlineCode parentName="p">{`ToggleRow`}</inlineCode>{` renders as many table cells as you ask it to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const ToggleRow = ({ n }) => (
  <row>
    {new Array(n).fill(0).map((_) => (
      <cell>
        <locktoggle></locktoggle>
      </cell>
    ))}
  </row>
);
`}</code></pre>
    <p>{`Notice the lack of prop drilling. `}<inlineCode parentName="p">{`<ToggleRow>`}</inlineCode>{` gets only a number, `}<inlineCode parentName="p">{`n`}</inlineCode>{`. Nothing about callbacks or the locked state. It doesn't even pass any props into `}<inlineCode parentName="p">{`<LockToggle>`}</inlineCode>{`.`}</p>
    <p>{`That's because `}<inlineCode parentName="p">{`LockToggle`}</inlineCode>{` is a smart context component 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const LockToggle = () => (
  <lockcontext class="consumer">
    {({ locked, toggleLock }) => (
      <button onclick={toggleLock} style={{ fontsize: "1.5em" }}>
        {locked ? "Unlock" : "Lock"}
      </button>
    )}
  </lockcontext>
);
`}</code></pre>
    <p>{`LockToggle renders a context consumer, which takes a function as children render prop. This render prop gets the necessary `}<inlineCode parentName="p">{`locked`}</inlineCode>{` state and `}<inlineCode parentName="p">{`toggleLock`}</inlineCode>{` callback in its arguments and returns a button that flips the lock.`}</p>
    <p>{`Et voilà: shared lock/toggle between components without prop drilling.`}</p>
    <p><img parentName="p" {...{
        "src": "/482c119ee84c855a7c5a53bb80edf0e6/media-krhB8ydCQiYZq-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`I also answered a lot of questions about when you should use context versus Redux or MobX. Watch the stream 😉`}</p>
    <h2 {...{
      "id": "what-swiz-learned"
    }}>{`What Swiz learned`}</h2>
    <p>{`Here's what I got out of the experience in a nutshell 1. Waiting for people to show up is nerve wracking 2. Lightly advertise in advance and people `}<em parentName="p">{`will`}</em>{` show up 3. Start the stream on time. no need to be there the first 10 minutes 4. You can wing it if you know your shit 5. Good way to practice conference talks, I think 6. Maybe have some slides 7. Drawing ideas out on paper while explaining them is a great interactive way to produce slides in real time 8. The delay between what you say and what people hear is massive 9. Giving the audience time to ask questions is nerve wracking`}</p>
    <h2 {...{
      "id": "whats-next"
    }}>{`What's next`}</h2>
    <p>{`More webinars I think. Great way to develop new content and a low friction approach to video production. That gives me ideas. 🤔`}</p>
    <p>{`Plus, answering questions from the audience is a great way to see what you forgot to cover. That's slope right there!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      